import React from "react";
import {
  Building2, MapPin, Globe2, Layers, Sparkles, ArrowUpRight, ShieldCheck,
  Compass, Briefcase, Trees, Network,
} from "lucide-react";

const CDX = {
  name: "CDX Premium Developers s.r.o.",
  role: "Strategický, developerský a technologicko-podnikatelský partner",
  claim: "Developerské a strategické zázemí s projektem Rezidence Oskava a mezinárodní vizí.",
  description:
    "CDX Premium Developers je moderní developerská a investiční skupina zaměřená na rezidenční projekty, strategické investiční příležitosti a dlouhodobou hodnotu. V rámci ekosystému STK Komfort představuje strategické, podnikatelské a technologické zázemí, které pomáhá budovat profesionální systém, značku, procesy a dlouhodobý rozvoj služeb.",
  description2:
    "Konkrétní ukázkou developerského přístupu CDX je Rezidence Oskava — komorní projekt pěti moderních vil v soukromém areálu s důrazem na architekturu, soukromí, přírodu a dlouhodobou hodnotu. Vedle evropských aktivit CDX zároveň rozvíjí mezinárodní vizi a připravuje strategická jednání v perspektivních lokalitách Latinské Ameriky.",
  areas: [
    "Moderní rezidenční development",
    "Projektové řízení",
    "Strategické investiční projekty",
    "Transparentní modely",
    "Podnikatelské a technologické zázemí",
    "Budování dlouhodobé hodnoty",
    "Rezidence Oskava",
    "Mezinárodní projektová vize",
  ],
  cdxHref: "https://www.cdxpremium.com/cs",
  oskavaHref: "https://www.cdxpremium.com/cs/rezidence-oskava",
  oskavaPreorderHref: "https://www.cdxpremium.com/cs/predprodej-domu-oskava",
};

const OSKAVA_BULLETS = [
  "Komorní rezidenční projekt",
  "Pět moderních vil",
  "Soukromý areál s vlastní komunikací",
  "Parkování a soukromé zahrady",
  "Nadčasová architektura",
  "Bydlení v harmonii s přírodou",
  "Důraz na klid, bezpečí a komfort",
  "Transparentní model koupě",
  "Projekt pod dohledem CDX Premium Developers",
];

const DIFFERENTIATORS = [
  "Více než dvě dekády odborné praxe ve stavebnictví, technických profesích a řízení projektů",
  "Propojení developmentu, investic, technologií a projektového řízení",
  "Důraz na transparentní strukturu a dlouhodobou hodnotu",
  "Schopnost připravovat projekty od konceptu až po realizaci",
  "Vlastní rezidenční projekt Rezidence Oskava",
  "Mezinárodní vize směrem do Latinské Ameriky",
  "Strategická jednání a připravované příležitosti v Kostarice, Mexiku a Dominikánské republice",
  "Budování širšího podnikatelského a partnerského ekosystému",
];

const LOCATIONS = [
  { label: "Rezidence Oskava", note: "Česká republika", strong: true },
  { label: "Česká republika", note: "Realizace" },
  { label: "Slovensko", note: "Rozvoj" },
  { label: "Kostarika", note: "Připravované příležitosti" },
  { label: "Mexiko", note: "Strategická jednání" },
  { label: "Dominikánská republika", note: "Budoucí potenciál" },
];

const OSKAVA_IMPORTANCE = [
  "CDX pracuje s projektem jako s celkem, ne jen s jednotlivou stavbou.",
  "Důraz je kladen na kvalitu prostředí, soukromí a dlouhodobou hodnotu.",
  "Projekt kombinuje moderní architekturu, technickou přípravu a transparentní obchodní model.",
  "Klient získává jasnější strukturu, postupné kroky a projekt pod dohledem developera.",
  "Oskava podporuje positioning CDX jako prémiové developerské skupiny s vlastním know-how.",
];

export default function StrategickyPartner() {
  return (
    <section
      id="strategicky-partner"
      data-testid="strategicky-partner-section"
      className="relative overflow-hidden bg-[#06070D] text-white py-24 lg:py-28"
    >
      {/* premium ambient layers */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.55) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at top, black 30%, transparent 75%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-0 h-[480px] w-[480px] rounded-full blur-3xl opacity-[0.10]"
        style={{ background: "radial-gradient(closest-side, #B8862E, transparent)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/[0.06] px-3 py-1 text-xs uppercase tracking-[0.24em] text-amber-300">
            <Sparkles className="h-3.5 w-3.5" />
            Strategický partner ekosystému
          </div>
          <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
            CDX Premium Developers
            <span className="block text-amber-300/90 mt-1">Developerské zázemí a mezinárodní vize</span>
          </h2>
          <p className="font-body mt-5 text-lg text-slate-300 leading-relaxed">
            {CDX.claim}
          </p>
        </div>

        {/* MAIN CARD */}
        <div
          data-testid="cdx-main-card"
          className="mt-12 rounded-3xl border border-amber-300/15 bg-gradient-to-br from-[#0F1730] via-[#0A1024] to-[#06070D] p-8 sm:p-10 lg:p-12 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-300/30 bg-amber-300/10">
                  <Building2 className="h-6 w-6 text-amber-300" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight">
                    {CDX.name}
                  </h3>
                  <div className="text-xs uppercase tracking-[0.2em] text-amber-300/80 mt-1.5">
                    {CDX.role}
                  </div>
                </div>
              </div>

              <p className="mt-7 text-slate-300 leading-relaxed text-[15px]">
                {CDX.description}
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed text-[15px]">
                {CDX.description2}
              </p>

              {/* Areas */}
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/80 font-semibold">
                  Klíčové oblasti
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {CDX.areas.map((a) => (
                    <span
                      key={a}
                      className="rounded-full border border-amber-300/15 bg-amber-300/[0.04] px-3 py-1 text-xs text-slate-200"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href={CDX.cdxHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="cdx-cta-website"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-[0_10px_24px_-8px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-transform"
                >
                  Navštívit CDX Premium Developers
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href={CDX.oskavaHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="cdx-cta-oskava"
                  className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/[0.08] px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-300/[0.16] transition-colors"
                >
                  Zjistit více o Rezidenci Oskava
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Right column — Differentiators */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-7 h-full">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
                  <Compass className="h-3.5 w-3.5" />
                  Čím se CDX odlišuje
                </div>
                <h3 className="font-heading mt-3 text-xl font-bold text-white leading-snug">
                  Více než developer — projektová struktura, hodnota, ekosystém
                </h3>
                <ul className="mt-6 space-y-3">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="flex gap-3 text-[13px] text-slate-300 leading-relaxed">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 pt-5 border-t border-white/5 text-xs text-amber-200/80 italic leading-relaxed">
                  „CDX Premium Developers nevytváří pouze stavby. Vytváří
                  projektové struktury, hodnotu a dlouhodobě řízený
                  developerský ekosystém.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SUB BLOCKS — 3-up grid */}
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {/* Block A — Rezidence Oskava */}
          <div
            data-testid="cdx-oskava-block"
            className="rounded-3xl border border-amber-300/20 bg-gradient-to-br from-[#0F1730] to-[#06070D] p-7 sm:p-8 lg:col-span-2"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
              <Trees className="h-3.5 w-3.5" />
              Aktuální developerský projekt
            </div>
            <h3 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-white leading-tight">
              Rezidence Oskava
            </h3>
            <p className="mt-2 text-amber-200/90 text-sm">
              Komorní rezidenční projekt moderních vil v přírodě.
            </p>
            <p className="mt-6 text-slate-300 leading-relaxed text-[15px]">
              CDX Rezidence Oskava představuje komorní rezidenční projekt
              navržený jako soukromý areál s vlastní komunikací, parkováním
              a soukromými zahradami. Projekt tvoří pět moderních vil
              s důrazem na nadčasovou architekturu, klidné prostředí,
              komfortní bydlení, soukromí a dlouhodobou hodnotu.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed text-[15px]">
              Rezidence Oskava ukazuje, že CDX Premium Developers nestaví
              pouze na myšlenkách, ale na konkrétních developerských
              projektech, které propojují architekturu, technickou
              přípravu, projektové řízení, financování, transparentní
              model a dlouhodobou hodnotu pro klienty.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
              {OSKAVA_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-200">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={CDX.oskavaHref}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="cdx-oskava-cta-detail"
                className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/[0.08] px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-300/[0.16] transition-colors"
              >
                Zjistit více o Rezidenci Oskava
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={CDX.oskavaPreorderHref}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="cdx-oskava-cta-preorder"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.08] transition-colors"
              >
                Předprodej domů Oskava
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={CDX.cdxHref}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="cdx-oskava-cta-cdx"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.08] transition-colors"
              >
                Navštívit projekt CDX
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Block B — Why Oskava matters */}
          <div
            data-testid="cdx-importance-block"
            className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-8"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" />
              Proč je Oskava důležitá
            </div>
            <h3 className="font-heading mt-3 text-xl font-bold text-white leading-snug">
              Strategie, dlouhodobost, hodnota
            </h3>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              Rezidence Oskava je ukázkou přístupu CDX Premium Developers
              k modernímu developmentu. Nejde pouze o výstavbu domů, ale
              o vytvoření promyšleného rezidenčního prostředí s důrazem
              na architekturu, soukromí, přírodu, praktickou dostupnost
              a dlouhodobou hodnotu.
            </p>
            <ul className="mt-6 space-y-2.5">
              {OSKAVA_IMPORTANCE.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-slate-300 leading-relaxed">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300/80" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 pt-5 border-t border-white/5 text-xs text-amber-200/80 italic leading-relaxed">
              „Rezidence Oskava představuje konkrétní ukázku toho, jak CDX
              Premium Developers přemýšlí o developmentu: strategicky,
              dlouhodobě a s důrazem na hodnotu projektu.&rdquo;
            </p>
          </div>
        </div>

        {/* INTERNATIONAL VISION */}
        <div
          data-testid="cdx-international-block"
          className="mt-10 rounded-3xl border border-amber-300/15 bg-gradient-to-br from-[#0A1024] via-[#0A1024] to-[#06070D] p-8 sm:p-10"
        >
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
                <Globe2 className="h-3.5 w-3.5" />
                Mezinárodní vize
              </div>
              <h3 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-white leading-tight">
                Od Rezidence Oskava po mezinárodní projekty
              </h3>
              <p className="mt-5 text-slate-300 leading-relaxed text-[15px]">
                Rezidence Oskava představuje konkrétní rezidenční projekt
                v České republice. Zároveň CDX Premium Developers rozvíjí
                širší mezinárodní vizi a sleduje nové projektové
                příležitosti v atraktivních zahraničních lokalitách, jako
                jsou Kostarika, Mexiko a Dominikánská republika. Tato
                kombinace lokální realizace a mezinárodního směru
                odlišuje CDX od běžných developerských firem zaměřených
                pouze na jeden trh.
              </p>

              <p className="mt-5 text-xs text-slate-400 leading-relaxed">
                Zahraniční aktivity jsou prezentovány jako{" "}
                <span className="text-amber-300/90">připravované příležitosti</span>,{" "}
                <span className="text-amber-300/90">strategická jednání</span> a{" "}
                <span className="text-amber-300/90">budoucí potenciál</span>.
                Nejedná se o hotové ani garantované projekty.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" />
                Lokality v rámci ekosystému
              </div>
              <ul className="space-y-2.5">
                {LOCATIONS.map((l) => (
                  <li
                    key={l.label}
                    className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${
                      l.strong
                        ? "border-amber-300/40 bg-amber-300/[0.08] text-amber-100"
                        : "border-white/10 bg-white/[0.02] text-slate-200"
                    }`}
                  >
                    <span className="font-semibold">{l.label}</span>
                    <span className="text-[11px] uppercase tracking-[0.16em] opacity-80">
                      {l.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-5">
          <div className="flex items-start gap-3 text-sm text-slate-400 max-w-2xl leading-relaxed">
            <Network className="h-4 w-4 text-amber-300/80 mt-0.5 shrink-0" />
            <span>
              CDX Premium Developers s.r.o. <strong className="text-slate-200">není autoservis ani STK stanice</strong>.
              V rámci ekosystému STK Komfort vystupuje jako strategický,
              developerský a technologicko-podnikatelský partner. Rezidence
              Oskava není služba STK Komfort — jde o samostatný developerský
              projekt CDX.
            </span>
          </div>
          <a
            href={CDX.cdxHref}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="cdx-footer-link"
            className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/[0.08] px-4 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-300/[0.16]"
          >
            <Briefcase className="h-3.5 w-3.5" />
            Poznat strategické zázemí projektu
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* unused-import safety */}
      <span className="hidden">
        <Layers />
      </span>
    </section>
  );
}
